import { useState, useEffect } from 'react';
import { Plus, Search, Filter, FolderOpen, Users, Calendar, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ProjectService } from '../services/projectService';
import { CreateProjectModal, ProjectDetailView, LoadingSpinner } from '../components';
import { Project } from '../types';

export default function ProjectManagement() {
  const { firebaseUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed' | 'archived'>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [projectTasks, setProjectTasks] = useState<Map<string, number[]>>(new Map()); // projectId -> [totalTasks, completedTasks]

  const projectService = ProjectService.getInstance();

  useEffect(() => {
    loadProjects();
  }, [firebaseUser]);

  const loadProjects = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError('');
      
      console.log('📋 Loading user projects...');
      const userProjects = await projectService.getUserProjects(firebaseUser.uid);
      
      console.log('✅ Loaded projects:', userProjects.length);
      setProjects(userProjects);
      
      // Load task counts for each project
      await loadProjectTaskCounts(userProjects);
    } catch (err: any) {
      console.error('❌ Error loading projects:', err);
      setError(`Failed to load projects: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectTaskCounts = async (projects: Project[]) => {
    const taskCountsMap = new Map<string, number[]>();
    
    try {
      // Load task counts for all projects in parallel
      const taskCountPromises = projects.map(async (project) => {
        try {
          const tasks = await projectService.getProjectTasks(project.id);
          const totalTasks = tasks.length;
          const completedTasks = tasks.filter(task => task.status === 'completed').length;
          return { projectId: project.id, counts: [totalTasks, completedTasks] };
        } catch (error) {
          console.warn('Failed to load tasks for project:', project.id);
          return { projectId: project.id, counts: [0, 0] };
        }
      });

      const taskCounts = await Promise.all(taskCountPromises);
      
      taskCounts.forEach(({ projectId, counts }) => {
        taskCountsMap.set(projectId, counts);
      });

      setProjectTasks(taskCountsMap);
      console.log('✅ Loaded task counts for', taskCounts.length, 'projects');
    } catch (err) {
      console.error('❌ Error loading project task counts:', err);
      // Don't fail the whole operation if task counts fail
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Project['status']) => {
    switch (status) {
      case 'active': return <Clock className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'archived': return <FolderOpen className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    console.log('Project selected:', project);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
  };

  const handleCreateProject = () => {
    setIsCreateModalOpen(true);
  };

  const handleProjectCreated = (newProject: Project) => {
    setProjects(prevProjects => [newProject, ...prevProjects]);
    console.log('Project created and added to list:', newProject);
  };

  const handleProjectUpdated = (updatedProject: Project) => {
    setProjects(prevProjects => 
      prevProjects.map(project => 
        project.id === updatedProject.id ? updatedProject : project
      )
    );
    setSelectedProject(updatedProject); // Update the selected project too
    console.log('Project updated:', updatedProject);
  };

  const handleProjectDeleted = (deletedProjectId: string) => {
    setProjects(prevProjects => 
      prevProjects.filter(project => project.id !== deletedProjectId)
    );
    setSelectedProject(null); // Go back to projects list
    console.log('Project deleted:', deletedProjectId);
  };

  const refreshProjectTaskCounts = async (projectId: string) => {
    try {
      console.log('🔄 Refreshing task counts for project:', projectId);
      const tasks = await projectService.getProjectTasks(projectId);
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter(task => task.status === 'completed').length;
      
      setProjectTasks(prevTaskCounts => {
        const newTaskCounts = new Map(prevTaskCounts);
        newTaskCounts.set(projectId, [totalTasks, completedTasks]);
        return newTaskCounts;
      });
      
      console.log('✅ Updated task counts for project:', projectId, 'Total:', totalTasks, 'Completed:', completedTasks);
    } catch (err) {
      console.warn('Failed to refresh task counts for project:', projectId, err);
    }
  };

  // Show project detail view if a project is selected
  if (selectedProject) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProjectDetailView 
          project={selectedProject}
          onBack={handleBackToProjects}
          onProjectUpdate={handleProjectUpdated}
          onProjectDelete={handleProjectDeleted}
          onTasksChanged={() => refreshProjectTaskCounts(selectedProject.id)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <LoadingSpinner message="Loading projects..." color="green" fullPage />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FolderOpen className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
                <p className="text-lg text-gray-600 mt-1">
                  Organize your work with projects, tasks, and Drive integration
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateProject}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Project
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Projects</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' ? 'No matching projects' : 'No projects yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first project to get started with organizing your work'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={handleCreateProject}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Project
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="p-6">
                  {/* Project Header */}
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                      {project.name}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                      {getStatusIcon(project.status)}
                      <span className="ml-1 capitalize">{project.status}</span>
                    </span>
                  </div>

                  {/* Project Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {project.description || 'No description provided'}
                  </p>

                  {/* Drive Folder Link */}
                  {project.driveFolder && (
                    <div className="flex items-center text-sm text-blue-600 mb-4">
                      <FolderOpen className="w-4 h-4 mr-2" />
                      <span className="truncate">{project.driveFolder.name}</span>
                    </div>
                  )}

                  {/* Project Metadata */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Updated {new Date(project.updatedAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span>You</span>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      {(() => {
                        const taskCounts = projectTasks.get(project.id) || [0, 0];
                        const [totalTasks, completedTasks] = taskCounts;
                        const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                        
                        return (
                          <div className="flex-1">
                            <span className="text-sm text-gray-500">
                              Tasks: {totalTasks} • Complete: {completionPercentage}%
                            </span>
                            {totalTasks > 0 && (
                              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    completionPercentage === 100 ? 'bg-green-500' :
                                    completionPercentage >= 75 ? 'bg-blue-500' :
                                    completionPercentage >= 50 ? 'bg-yellow-500' :
                                    'bg-gray-400'
                                  }`}
                                  style={{ width: `${completionPercentage}%` }}
                                ></div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div className="ml-4">
                        <button className="text-green-600 hover:text-green-700 text-sm font-medium">
                          Open
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {projects.filter(p => p.status === 'active').length}
            </div>
            <div className="text-sm text-gray-600">Active Projects</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {projects.filter(p => p.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-600">Completed Projects</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-gray-600 mb-2">
              {Array.from(projectTasks.values()).reduce((total, [taskCount]) => total + taskCount, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Tasks</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-purple-600 mb-2">
              {projects.filter(p => p.driveFolder).length}
            </div>
            <div className="text-sm text-gray-600">With Drive Links</div>
          </div>
        </div>
      </main>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
}
